import { Component, computed, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { DateAdapter } from '@angular/material/core';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { TouristGuidesSalaryApiService } from '../_api/tourist-guides-salary.api.service';
import {
	getSalaryTypeLabel,
	mapToSalaryViewModel,
	PaginatedResponse,
	PaymentMethod,
	TouristGuideSalaryFilters,
	TouristGuideSalaryViewModel,
} from '../_models/tourist-guides-salary.model';
import { PricePipe } from '@/common/pipes/price.pipe';
import { DateService } from '@/common/date.service';
import { catchError, EMPTY, finalize } from 'rxjs';

// Map frontend column names to API field names,
const SORT_FIELD_MAP: Record<string, string> = {
	guideName: 'guide_name',
	eventName: 'event_name',
	eventDate: 'event_date_from',
	vehicle: 'vehicle',
	invoice: 'invoice_number',
	paymentDueDate: 'payment_due_date',
	transferDueDate: 'transfer_due_date',
};

const SORT_COLUMN_MAP: Record<string, string> = Object.fromEntries(
	Object.entries(SORT_FIELD_MAP).map(([k, v]) => [v, k]),
);

const CSV_HEADERS = [
	'Pilot',
	'Wydarzenie',
	'Typ',
	'Pojazd',
	'Data od',
	'Data do',
	'Wynagrodzenie',
	'Dodatki',
	'Potrącenia',
	'Całkowite obciążenie',
	'Do wypłaty',
	'Płatności',
	'Umowa potrzebna',
	'Status rozliczenia',
	'Termin płatności',
	'Termin przelewu',
	'Umowa/FV',
	'Komentarz',
];

@Component({
	selector: 'app-tourist-guides-salary-list',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		RouterLink,
		NavbarModule,
		MatCardModule,
		MatTableModule,
		MatButtonModule,
		MatSortModule,
		MatProgressSpinnerModule,
		MatPaginatorModule,
		MatIconModule,
		MatTooltipModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatDatepickerModule,
		MatTabsModule,
		MatSnackBarModule,
		MatMenuModule,
		DatePipe,
		PricePipe,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }, DatePipe],
	templateUrl: './tourist-guides-salary-list.component.html',
	styleUrl: './tourist-guides-salary-list.component.scss',
})
export class TouristGuidesSalaryListComponent implements OnInit {
	// Optional input - when provided, component works in "embedded" mode (e.g., pilot's salary tab)
	touristGuideId = input<string>();
	isEmbedded = computed(() => !!this.touristGuideId());

	table = viewChild<MatTable<TouristGuideSalaryViewModel>>('dataTable');
	sort = viewChild(MatSort);
	paginator = viewChild(MatPaginator);
	matData = new MatTableDataSource<TouristGuideSalaryViewModel>([]);

	private static readonly HIDDEN_COLUMNS_STORAGE_KEY = 'tg-salary-list-hidden-columns';

	readonly toggleableColumns: { id: string; label: string }[] = [
		{ id: 'eventName', label: 'Wydarzenie' },
		{ id: 'vehicle', label: 'Pojazd' },
		{ id: 'eventDate', label: 'Data wydarzenia' },
		{ id: 'salary', label: 'Wynagrodzenie' },
		{ id: 'totalBurden', label: 'Całkowite obciążenie' },
		{ id: 'toPayAmount', label: 'Do wypłaty' },
		{ id: 'payments', label: 'Płatności' },
		{ id: 'contractNeeded', label: 'Umowa potrzebna' },
		{ id: 'settlementStatus', label: 'Status rozliczenia' },
		{ id: 'paymentDueDate', label: 'Termin płatności' },
		{ id: 'transferDueDate', label: 'Termin przelewu' },
		{ id: 'invoice', label: 'Umowa/FV' },
		{ id: 'comment', label: 'Komentarz' },
	];

	hiddenColumns = signal<Set<string>>(this.loadHiddenColumns());

	displayedColumns = computed(() => {
		const visible = this.toggleableColumns.map((column) => column.id).filter((id) => !this.hiddenColumns().has(id));
		if (this.isEmbedded()) {
			return [...visible, 'action'];
		}
		return ['expand', 'guideName', ...visible, 'action'];
	});

	isColumnHidden(id: string): boolean {
		return this.hiddenColumns().has(id);
	}

	toggleColumn(id: string): void {
		const next = new Set(this.hiddenColumns());
		if (next.has(id)) {
			next.delete(id);
		} else {
			next.add(id);
		}
		this.hiddenColumns.set(next);
		this.persistHiddenColumns(next);
	}

	expandedElement = signal<TouristGuideSalaryViewModel | null>(null);
	salaries = signal<TouristGuideSalaryViewModel[]>([]);
	isLoading = signal(false);
	isCopying = signal(false);
	totalItems = signal(0);
	currentPage = signal(1);
	pageSize = signal(20);
	totalPages = signal(0);
	// Sorting
	sortActive = signal<string>(''); // column name for MatSort
	sortBy = signal<string | undefined>(undefined); // API field name
	sortDirection = signal<'asc' | 'desc' | ''>('');

	filterForm: FormGroup;
	selectedTabIndex = 0;
	readonly paymentTabs: { label: string; value: 'settled' | 'to_pay' | 'overdue' | 'pending' | null }[] = [
		{ label: 'Wszystkie', value: null },
		{ label: 'Do ustalenia', value: 'pending' },
		{ label: 'Do opłacenia', value: 'to_pay' },
		{ label: 'Rozliczone', value: 'settled' },
		{ label: 'Przeterminowane', value: 'overdue' },
	];
	readonly getTypeLabel = getSalaryTypeLabel;

	readonly paymentMethodLabels: Record<PaymentMethod, string> = {
		cash: 'Gotówka',
		transfer: 'Przelew',
	};

	toggleExpand(element: TouristGuideSalaryViewModel): void {
		this.expandedElement.update((current) => (current?.id === element.id ? null : element));
	}

	isExpanded(element: TouristGuideSalaryViewModel): boolean {
		return this.expandedElement()?.id === element.id;
	}

	private readonly apiService = inject(TouristGuidesSalaryApiService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly fb = inject(FormBuilder);
	private readonly dateService = inject(DateService);
	private readonly snackBar = inject(MatSnackBar);
	private readonly datePipe = inject(DatePipe);
	private readonly pricePipe = new PricePipe();
	private readonly SNACKBAR_DURATION = 3000;

	ngOnInit(): void {
		if (!this.isEmbedded()) {
			this.initFilterForm();
			this.initFromUrl();
		}
		this.loadSalaries();
	}

	loadSalaries(): void {
		this.isLoading.set(true);

		const filters: TouristGuideSalaryFilters = this.isEmbedded()
			? { sortBy: this.sortBy(), sortDirection: this.sortDirection() || undefined }
			: this.buildBaseFilters();
		filters.page = this.currentPage();
		filters.perPage = this.pageSize();

		const request$ = this.isEmbedded()
			? this.apiService.getSalaries(this.touristGuideId()!, filters)
			: this.apiService.getAllSalaries(filters);

		request$
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoading.set(false)),
			)
			.subscribe((response: PaginatedResponse) => {
				this.salaries.set(response.items.map(mapToSalaryViewModel));
				this.totalItems.set(response.total);
				this.totalPages.set(response.totalPages);
				this.refreshTable();
			});
	}

	copyColumns(): void {
		this.isCopying.set(true);

		const request$ = this.isEmbedded()
			? this.apiService.getSalaries(this.touristGuideId()!, {
					all: true,
					sortBy: this.sortBy(),
					sortDirection: this.sortDirection() || undefined,
				})
			: this.apiService.getAllSalaries({ ...this.buildBaseFilters(), all: true });

		request$
			.pipe(
				catchError(() => {
					this.snackBar.open('Wystąpił błąd podczas pobierania danych do skopiowania', 'Zamknij', {
						duration: this.SNACKBAR_DURATION,
					});
					return EMPTY;
				}),
				finalize(() => this.isCopying.set(false)),
			)
			.subscribe((response: PaginatedResponse) => {
				const csv = this.buildCsv(response.items.map(mapToSalaryViewModel));
				navigator.clipboard
					.writeText(csv)
					.then(() =>
						this.snackBar.open('Skopiowano do schowka', 'Zamknij', {
							duration: this.SNACKBAR_DURATION,
						}),
					)
					.catch(() =>
						this.snackBar.open('Wystąpił błąd podczas kopiowania do schowka', 'Zamknij', {
							duration: this.SNACKBAR_DURATION,
						}),
					);
			});
	}

	onFilter(): void {
		this.currentPage.set(1);
		if (!this.isEmbedded()) {
			this.updateUrl();
		}
		this.loadSalaries();
	}

	onClearFilters(): void {
		this.filterForm.reset({
			dateFrom: null,
			dateTo: null,
			guideSearch: '',
			eventSearch: '',
		});
		this.currentPage.set(1);
		if (!this.isEmbedded()) {
			this.updateUrl();
		}
		this.loadSalaries();
	}

	onTabChange(index: number): void {
		this.selectedTabIndex = index;
		this.currentPage.set(1);
		if (!this.isEmbedded()) {
			this.updateUrl();
		}
		this.loadSalaries();
	}

	onPageChange(event: PageEvent): void {
		this.currentPage.set(event.pageIndex + 1);
		this.pageSize.set(event.pageSize);
		if (!this.isEmbedded()) {
			this.updateUrl();
		}
		this.loadSalaries();
	}

	onSortChange(sort: Sort): void {
		if (sort.direction) {
			const apiField = SORT_FIELD_MAP[sort.active];
			if (apiField) {
				this.sortActive.set(sort.active);
				this.sortBy.set(apiField);
				this.sortDirection.set(sort.direction);
			}
		} else {
			this.sortActive.set('');
			this.sortBy.set(undefined);
			this.sortDirection.set('');
		}
		this.currentPage.set(1);
		if (!this.isEmbedded()) {
			this.updateUrl();
		}
		this.loadSalaries();
	}

	openSalaryForm(element: TouristGuideSalaryViewModel): void {
		if (this.isEmbedded()) {
			this.router.navigate(['/touristGuides', this.touristGuideId(), 'salary', element.id]);
		} else {
			const currentUrl = this.router.url;
			this.router.navigate(['/touristGuides', element.guideId, 'salary', element.id], {
				queryParams: { from: 'list', returnUrl: currentUrl },
			});
		}
	}

	private initFromUrl(): void {
		const params = this.route.snapshot.queryParams;

		const tab = params['tab'];
		if (tab) {
			const tabIndex = this.paymentTabs.findIndex((t) => t.value === tab || (tab === 'all' && t.value === null));
			if (tabIndex !== -1) {
				this.selectedTabIndex = tabIndex;
			}
		}

		if (params['dateFrom']) {
			this.filterForm.patchValue({ dateFrom: new Date(params['dateFrom']) });
		}
		if (params['dateTo']) {
			this.filterForm.patchValue({ dateTo: new Date(params['dateTo']) });
		}
		if (params['guideSearch']) {
			this.filterForm.patchValue({ guideSearch: params['guideSearch'] });
		}
		if (params['eventSearch']) {
			this.filterForm.patchValue({ eventSearch: params['eventSearch'] });
		}
		if (params['page']) {
			const page = parseInt(params['page'], 10);
			if (!isNaN(page) && page > 0) {
				this.currentPage.set(page);
			}
		}
		if (params['pageSize']) {
			const pageSize = parseInt(params['pageSize'], 10);
			if (!isNaN(pageSize) && [10, 20, 50, 100].includes(pageSize)) {
				this.pageSize.set(pageSize);
			}
		}
		if (params['sortBy']) {
			this.sortBy.set(params['sortBy']);
			const columnName = SORT_COLUMN_MAP[params['sortBy']];
			if (columnName) {
				this.sortActive.set(columnName);
			}
		}
		if (params['sortDirection'] && ['asc', 'desc'].includes(params['sortDirection'])) {
			this.sortDirection.set(params['sortDirection'] as 'asc' | 'desc');
		}
	}

	private updateUrl(): void {
		const formValue = this.filterForm.value;
		const queryParams: Record<string, string | null> = {};

		const tabValue = this.paymentTabs[this.selectedTabIndex].value;
		queryParams['tab'] = tabValue ?? 'all';
		queryParams['dateFrom'] = formValue.dateFrom ? this.formatDate(formValue.dateFrom) : null;
		queryParams['dateTo'] = formValue.dateTo ? this.formatDate(formValue.dateTo) : null;
		queryParams['guideSearch'] = formValue.guideSearch?.trim() || null;
		queryParams['eventSearch'] = formValue.eventSearch?.trim() || null;
		queryParams['page'] = this.currentPage() > 1 ? String(this.currentPage()) : null;
		queryParams['pageSize'] = this.pageSize() !== 20 ? String(this.pageSize()) : null;
		queryParams['sortBy'] = this.sortBy() || null;
		queryParams['sortDirection'] = this.sortDirection() || null;

		this.router.navigate([], {
			relativeTo: this.route,
			queryParams,
			queryParamsHandling: 'merge',
			replaceUrl: true,
		});
	}

	private initFilterForm(): void {
		this.filterForm = this.fb.group({
			dateFrom: [null],
			dateTo: [null],
			guideSearch: [''],
			eventSearch: [''],
		});
	}

	private translatePaginator(paginator: MatPaginator): void {
		paginator._intl.itemsPerPageLabel = 'Pozycji na stronę';
		paginator._intl.firstPageLabel = 'Pierwsza strona';
		paginator._intl.lastPageLabel = 'Ostatnia strona';
		paginator._intl.nextPageLabel = 'Następna strona';
		paginator._intl.previousPageLabel = 'Poprzednia strona';
		paginator._intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
			if (length === 0 || pageSize === 0) {
				return `0 z ${length}`;
			}
			const startIndex = page * pageSize;
			const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
			return `${startIndex + 1} - ${endIndex} z ${length}`;
		};
	}

	private formatDate(date: Date | string): string {
		if (typeof date === 'string') {
			return date.split(' ')[0];
		}
		return this.dateService.toUtcDate(date);
	}

	private buildBaseFilters(): TouristGuideSalaryFilters {
		const formValue = this.filterForm.value;
		const filters: TouristGuideSalaryFilters = {
			sortBy: this.sortBy(),
			sortDirection: this.sortDirection() || undefined,
		};

		if (formValue.dateFrom) {
			filters.dateFrom = this.formatDate(formValue.dateFrom);
		}
		if (formValue.dateTo) {
			filters.dateTo = this.formatDate(formValue.dateTo);
		}
		if (formValue.guideSearch?.trim()) {
			filters.guideSearch = formValue.guideSearch.trim();
		}
		if (formValue.eventSearch?.trim()) {
			filters.eventSearch = formValue.eventSearch.trim();
		}

		const paymentStatus = this.paymentTabs[this.selectedTabIndex].value;
		if (paymentStatus) {
			filters.paymentStatus = paymentStatus;
		}

		return filters;
	}

	private buildCsv(rows: TouristGuideSalaryViewModel[]): string {
		const table = [CSV_HEADERS, ...rows.map((row) => this.rowToCsvValues(row))];
		return table.map((cols) => cols.map((value) => this.sanitizeCsvValue(value)).join(';')).join('\n');
	}

	private rowToCsvValues(row: TouristGuideSalaryViewModel): string[] {
		return [
			row.guideName,
			row.eventName || '-',
			this.getTypeLabel(row.type),
			row.vehiclesDisplay,
			this.datePipe.transform(row.eventDateFrom, 'dd.MM.yyyy') ?? '',
			this.datePipe.transform(row.eventDateTo, 'dd.MM.yyyy') ?? '',
			row.salaryDisplay.hasValue ? this.formatAmounts(row.salaryDisplay.amounts) : 'Do ustalenia',
			this.formatAmounts(row.paymentDetails?.totalAdditions ?? []),
			this.formatAmounts(row.paymentDetails?.totalDeductions ?? []),
			row.totalBurden !== null && row.totalBurden !== undefined
				? `${this.formatAmount(row.totalBurden)} PLN`
				: '-',
			row.toPayDisplay?.amounts?.length ? this.formatAmounts(row.toPayDisplay.amounts) : '-',
			this.formatPayments(row),
			this.formatContractNeeded(row.contractNeeded),
			this.formatSettlementStatus(row),
			this.datePipe.transform(row.paymentDueDate, 'dd.MM.yyyy') ?? '-',
			this.datePipe.transform(row.transferDueDate, 'dd.MM.yyyy') ?? '-',
			row.invoiceNumber,
			row.comment,
		];
	}

	private formatContractNeeded(value: boolean | null): string {
		if (value === true) {
			return 'Tak';
		}
		if (value === false) {
			return 'Nie';
		}
		return '-';
	}

	private formatSettlementStatus(row: TouristGuideSalaryViewModel): string {
		if (row.settlementStatus) {
			return 'Rozliczone';
		}
		if (row.paymentDueDate) {
			return `Nie rozliczono (do ${this.datePipe.transform(row.paymentDueDate, 'dd.MM.yyyy')})`;
		}
		return 'Data rozliczenia do ustalenia';
	}

	private formatPayments(row: TouristGuideSalaryViewModel): string {
		const items = row.paymentDetails?.paymentItems ?? [];
		if (items.length === 0) {
			return '-';
		}
		return items
			.map((item) => {
				const amount = `${this.formatAmount(item.amount)} ${item.currency}`;
				const method = this.paymentMethodLabels[item.paymentMethod];
				const date = item.paymentDate ? this.datePipe.transform(item.paymentDate, 'dd.MM.yyyy') : '-';
				const status = item.confirmed ? 'Rozliczone' : 'Oczekujące';
				return `${amount}, ${method}, ${date}, ${status}`;
			})
			.join(' | ');
	}

	private formatAmount(amount: number): string {
		return this.pricePipe.transform(amount).toFixed(2).replace('.', ',');
	}

	private formatAmounts(amounts: { amount: number; currency: string }[]): string {
		return amounts.map((item) => `${this.formatAmount(item.amount)} ${item.currency}`).join(', ');
	}

	private sanitizeCsvValue(value: string): string {
		let result = (value ?? '').toString().replace(/[\r\n]+/g, ' ');
		if (/^[=+\-@\t]/.test(result)) {
			result = `'${result}`;
		}
		if (result.includes(';') || result.includes('"')) {
			result = `"${result.replace(/"/g, '""')}"`;
		}
		return result;
	}

	private loadHiddenColumns(): Set<string> {
		try {
			const raw = localStorage.getItem(TouristGuidesSalaryListComponent.HIDDEN_COLUMNS_STORAGE_KEY);
			if (!raw) {
				return new Set();
			}
			const parsed: unknown = JSON.parse(raw);
			return Array.isArray(parsed) ? new Set(parsed.map(String)) : new Set();
		} catch {
			return new Set();
		}
	}

	private persistHiddenColumns(hidden: Set<string>): void {
		try {
			localStorage.setItem(
				TouristGuidesSalaryListComponent.HIDDEN_COLUMNS_STORAGE_KEY,
				JSON.stringify([...hidden]),
			);
		} catch {
			return;
		}
	}

	private refreshTable(): void {
		this.matData.data = this.salaries();
		const tableInstance = this.table();
		if (tableInstance) {
			tableInstance.renderRows();
		}
	}
}
